import React, { useState, useEffect, useMemo } from 'react';
import axios from 'axios';
import toast from 'react-hot-toast';
import { Product } from '../types/Product';
import { Button } from './ui/button';
import { Badge } from './ui/badge';
import { RefreshCw, Package, ChevronDown, ChevronUp, Barcode, History, User, Clock, CheckCircle, Edit, ArrowLeft, Palette, Trash2, Settings2, X, Ban } from 'lucide-react';
import RejectModal from './RejectModal';
import ForceStatusModal from './ForceStatusModal';
import ClosureRequestModal from './ClosureRequestModal';
import { useAuth } from '../contexts/AuthContext';
import { useRoleUsers } from '../hooks/useRoleUsers';
import { sortSizeEntries } from '../lib/sizeSort';

// Adım isimleri
const STEP_NAMES: Record<string, string> = {
  'draft': 'Üretim (Taslak)',
  'production_entry': 'Nebim (SKU Bekliyor)',
  'nebim_entry': 'Nebim (İşlemde)',
  'barcode_sent': 'Üretim (Üreticiye Gönderim)',
  'stock_entry': 'Depo (Stok Girişi)',
  'api_transfer': 'Yönetim (Ticimaxa Aktarım)',
  'content_entry': 'İçerik (Açıklama)',
  'category_setup': 'İçerik (Kategorileme)',
  'image_upload': 'Görsel (Yükleme)',
  'publish_pending': 'Yönetim (Yayın Bekliyor)',
  'published': 'Yönetim (Yayında)'
};

interface ProductTableProps {
  products: Product[];
  onRefresh: () => void;
  onSentToProducer?: (productId: string) => void;
  onNebimAction?: (productId: string) => void;
  onWarehouseAction?: (productId: string) => void;
  onReject?: (productId: string, targetStep: string, reason: string) => Promise<void>;
  onContentAction?: (productId: string) => void;
  onCategoryAction?: (productId: string) => void;
  onImageUpload?: (productId: string) => void;
  onErpTransfer?: (productId: string) => void;
  onPublish?: (productId: string) => void;
  onEdit?: (product: Product) => void;
  onDelete?: (product: Product) => void;
  onClosureRequest?: (productId: string, reason: string) => Promise<void>;
  showAllActions?: boolean;
}

const ProductTable: React.FC<ProductTableProps> = ({ 
  products, 
  onRefresh, 
  onSentToProducer, 
  onNebimAction, 
  onWarehouseAction, 
  onReject,
  onContentAction,
  onCategoryAction,
  onImageUpload,
  onErpTransfer,
  onPublish,
  onEdit,
  onDelete,
  onClosureRequest,
  showAllActions
}) => {
  const { user } = useAuth();
  const { getUsersByRole } = useRoleUsers();
  const [expandedRows, setExpandedRows] = useState<Set<string>>(new Set());
  const [userCache, setUserCache] = useState<Record<string, string>>({});
  const [rejectModal, setRejectModal] = useState<{
    show: boolean;
    product: Product | null;
  }>({ show: false, product: null });
  const [closureModal, setClosureModal] = useState<{
    show: boolean;
    product: Product | null;
  }>({ show: false, product: null });
  const [forceStatusModal, setForceStatusModal] = useState<{
    show: boolean;
    product: Product | null;
  }>({ show: false, product: null });

  // Ticimax ID düzenleme modalı
  const [ticimaxEditModal, setTicimaxEditModal] = useState<{
    show: boolean;
    product: Product | null;
  }>({ show: false, product: null });
  const [ticimaxEditData, setTicimaxEditData] = useState<{
    ticimaxProductId: number;
    ticimaxProductIds: Record<string, number>;
  }>({ ticimaxProductId: 0, ticimaxProductIds: {} });
  const [ticimaxSaving, setTicimaxSaving] = useState(false);

  // Sıralama — varsayılan: en son işlem yapılan ürün en üstte
  const [sortConfig, setSortConfig] = useState<{
    key: 'updatedAt' | null;
    direction: 'asc' | 'desc';
  }>({ key: 'updatedAt', direction: 'desc' });

  const sortedProducts = useMemo(() => {
    if (!sortConfig.key) return products;
    return [...products].sort((a, b) => {
      const aTime = new Date(a.updatedAt || a.createdAt || 0).getTime();
      const bTime = new Date(b.updatedAt || b.createdAt || 0).getTime();
      return sortConfig.direction === 'asc' ? aTime - bTime : bTime - aTime;
    });
  }, [products, sortConfig]);

  const toggleSort = (key: 'updatedAt') => {
    setSortConfig(prev => {
      if (prev.key !== key) return { key, direction: 'desc' };
      return { key, direction: prev.direction === 'desc' ? 'asc' : 'desc' };
    });
  };

  const getValidTicimaxIds = (product: Product): number[] => {
    const idsFromMap = Object.values(product.ticimaxProductIds || {}).filter(id => id > 0);
    const legacyId = product.ticimaxProductId;

    if (legacyId && legacyId > 0 && !idsFromMap.includes(legacyId)) {
      idsFromMap.unshift(legacyId);
    }

    return idsFromMap;
  };

  const hasValidTicimaxId = (product: Product): boolean => getValidTicimaxIds(product).length > 0;

  const openTicimaxEditModal = (product: Product) => {
    const existingIds = { ...(product.ticimaxProductIds || {}) };
    if (product.ticimaxProductId && !Object.keys(existingIds).length && product.productionInfo?.colors?.length) {
      existingIds[product.productionInfo.colors[0].code] = product.ticimaxProductId;
    }
    setTicimaxEditData({
      ticimaxProductId: product.ticimaxProductId || 0,
      ticimaxProductIds: existingIds
    });
    setTicimaxEditModal({ show: true, product });
  };

  const handleTicimaxSave = async () => {
    if (!ticimaxEditModal.product) return;
    const product = ticimaxEditModal.product;
    const colors = product.productionInfo?.colors || [];
    const hasMultipleColors = colors.length > 0;

    if (hasMultipleColors) {
      const filledIds = Object.values(ticimaxEditData.ticimaxProductIds).filter(id => id > 0);
      if (filledIds.length === 0) {
        toast.error('En az bir varyant için Ticimax Ürün ID giriniz!');
        return;
      }
    } else {
      if (!ticimaxEditData.ticimaxProductId || ticimaxEditData.ticimaxProductId <= 0) {
        toast.error('Ticimax Ürün ID giriniz!');
        return;
      }
    }

    setTicimaxSaving(true);
    try {
      await axios.post(`/products/${product._id}/erp-transfer`, {
        ticimaxProductId: hasMultipleColors
          ? Object.values(ticimaxEditData.ticimaxProductIds).find(id => id > 0) || 0
          : ticimaxEditData.ticimaxProductId,
        ticimaxProductIds: hasMultipleColors ? ticimaxEditData.ticimaxProductIds : undefined
      });
      toast.success('Ticimax ID\'leri güncellendi!');
      setTicimaxEditModal({ show: false, product: null });
      onRefresh();
    } catch (error) {
      toast.error('Güncelleme başarısız!');
    } finally {
      setTicimaxSaving(false);
    }
  };
  
  // BASİT VE ANLAŞILIR YETKİ SİSTEMİ
  // Her departman SADECE kendi sorumlu olduğu aşamadan geri yollayabilir
  const canRejectProduct = (product: Product): boolean => {
    if (!user || !product || product.status === 'draft') return false;
    // Kapatılmış ürünler için geri yollama aksiyonu gösterme — ürün artık canlı değil
    if (product.status === 'published' && product.isActive === false) return false;

    // DEPARTMAN - AŞAMA EŞLEŞMESİ
    const rejectPermissions: Record<string, string[]> = {
      'production': ['barcode_sent'],                                      // Üreticide olanları geri alabilir
      'nebim': ['production_entry', 'nebim_entry'],                        // SKU aşamalarını geri yollayabilir
      'warehouse': ['stock_entry'],                                        // Depodakileri geri yollayabilir
      'content': ['content_entry', 'category_setup'],                      // İçerik aşamalarını geri yollayabilir
      'visual': ['image_upload'],                                          // Görsel aşamasını geri yollayabilir
      'publish': ['publish_pending'],                                      // Yayın bekleyenleri geri yollayabilir
      'admin': ['api_transfer', 'publish_pending', 'published'],           // Admin aşamalarını geri yollayabilir
      'erp': ['api_transfer', 'publish_pending', 'published']              // ERP kullanıcıları da admin gibi davranır
    };
    
    return rejectPermissions[user.role]?.includes(product.status) || false;
  };
  
  // Kullanıcı ID'sinden görüntülenecek ismi oluştur
  const getUserDisplayName = (userId: string) => {
    // Cache'de varsa onu kullan
    if (userCache[userId]) {
      return userCache[userId];
    }
    
    // ID'nin son 6 karakterini göster
    if (!userId) return 'Sistem';
    return `Kullanıcı #${userId.slice(-6)}`;
  };
  
  // Ürünlerden kullanıcı bilgilerini çıkar ve cache'e kaydet
  useEffect(() => {
    const cache: Record<string, string> = {};
    
    products.forEach(product => {
      // createdBy ve currentAssignee'den kullanıcı bilgilerini al
      if (product.createdBy && typeof product.createdBy === 'object') {
        const user = product.createdBy as any;
        if (user._id && user.name) {
          cache[user._id] = user.name;
        }
      }
      
      if (product.currentAssignee && typeof product.currentAssignee === 'object') {
        const user = product.currentAssignee as any;
        if (user._id && user.name) {
          cache[user._id] = user.name;
        }
      }
      
      // workflowHistory'den populate edilmiş kullanıcıları al
      product.workflowHistory?.forEach(step => {
        if (step.completedBy && typeof step.completedBy === 'object') {
          const user = step.completedBy as any;
          if (user._id && user.name) {
            cache[user._id] = user.name;
          }
        }
      });
    });
    
    setUserCache(cache);
  }, [products]);
  
  const getDepartmentInfo = (status: string) => {
    const personMap: Record<string, string> = {
      'draft': getUsersByRole('production') || 'Üretim',
      'production_entry': getUsersByRole('nebim') || 'Nebim',
      'nebim_entry': getUsersByRole('nebim') || 'Nebim',
      'barcode_sent': getUsersByRole('production') || 'Üretim',
      'stock_entry': getUsersByRole('warehouse') || 'Depo',
      'api_transfer': getUsersByRole('erp') || 'ERP',
      'content_entry': getUsersByRole('content') || 'İçerik',
      'category_setup': getUsersByRole('content') || 'İçerik',
      'image_upload': getUsersByRole('visual') || 'Görsel',
      'publish_pending': getUsersByRole('publish') || 'Yayın',
      'published': getUsersByRole('erp') || 'ERP'
    };
    const departments: Record<string, any> = {
      'draft': { name: 'Üretim / Beklemede', person: personMap['draft'] },
      'production_entry': { name: 'Nebim / SKU Bekliyor', person: personMap['production_entry'] },
      'nebim_entry': { name: 'Nebim / İşlemde', person: personMap['nebim_entry'] },
      'barcode_sent': { name: 'Üretim / Üreticide', person: personMap['barcode_sent'] },
      'stock_entry': { name: 'Depo / Stok Girişi', person: personMap['stock_entry'] },
      'api_transfer': { name: 'Yönetim / Ticimaxa Aktarım', person: personMap['api_transfer'] },
      'content_entry': { name: 'İçerik / Açıklama', person: personMap['content_entry'] },
      'category_setup': { name: 'İçerik / Kategorileme', person: personMap['category_setup'] },
      'image_upload': { name: 'Görsel / Yükleme', person: personMap['image_upload'] },
      'publish_pending': { name: 'Yönetim / Yayın Bekliyor', person: personMap['publish_pending'] },
      'published': { name: 'Yönetim / Yayında ✓', person: personMap['published'] }
    };
    return departments[status] || { name: 'Bilinmiyor', person: '-' };
  };

  const formatDate = (date: Date | string) => {
    const d = new Date(date);
    return d.toLocaleString('tr-TR', {
      day: '2-digit',
      month: '2-digit',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };
  
  const toggleRowExpansion = (productId: string) => {
    const newExpanded = new Set(expandedRows);
    if (newExpanded.has(productId)) {
      newExpanded.delete(productId);
    } else {
      newExpanded.add(productId);
    }
    setExpandedRows(newExpanded);
  };
  
  // Get previous workflow steps for reject modal
  const getPreviousSteps = (currentStatus: string): string[] => {
    const workflow = [
      'draft',
      'production_entry',
      'nebim_entry',
      'barcode_sent',
      'stock_entry',
      'api_transfer',
      'content_entry',
      'category_setup',
      'image_upload',
      'publish_pending',
      'published'
    ];
    
    const currentIndex = workflow.indexOf(currentStatus);
    if (currentIndex <= 0) return [];
    
    // TÜM önceki adımları döndür (herhangi bir önceki adıma gönderebilsin)
    return workflow.slice(0, currentIndex);
  };

  const getPriceDisplay = (product: Product) => {
    const salePrice = product.productionInfo?.salePrice;
    const discountPrice = product.productionInfo?.discountPrice;
    
    if (discountPrice && salePrice && discountPrice < salePrice) {
      const discountPercent = Math.round((1 - discountPrice / salePrice) * 100);
      return (
        <div className="flex items-center gap-2">
          <div>
            <div className="font-medium text-destructive">₺{discountPrice}</div>
            <div className="text-xs text-muted-foreground line-through">₺{salePrice}</div>
          </div>
          <Badge variant="destructive" className="text-xs">
            %{discountPercent}
          </Badge>
        </div>
      );
    } else if (salePrice) {
      return <span className="font-medium">₺{salePrice}</span>;
    }
    return <span className="text-muted-foreground">-</span>;
  };

  if (products.length === 0) {
    return (
      <div className="p-12 text-center">
        <div className="flex flex-col items-center text-muted-foreground">
          <Package className="w-16 h-16 mb-4" />
          <p className="text-lg font-medium mb-2">Ürün bulunamadı</p>
          <p className="text-sm">Filtreleri değiştirmeyi deneyin</p>
        </div>
      </div>
    );
  }

  return (
    <div className="p-6">
      <div className="flex items-center justify-between mb-4">
        <h3 className="text-lg font-semibold">Ürün Listesi</h3>
        <Button variant="ghost" size="sm" onClick={onRefresh}>
          <RefreshCw className="w-4 h-4" />
        </Button>
      </div>
      
      <div className="overflow-x-auto">
        <table className="w-full">
          <thead>
            <tr className="border-b bg-gray-50">
              <th className="text-left py-4 px-4 font-semibold text-sm text-gray-700">Ürün Bilgileri</th>
              <th className="text-left py-4 px-4 font-semibold text-sm text-gray-700">SKU / Barkod</th>
              <th className="text-left py-4 px-4 font-semibold text-sm text-gray-700">Kategori</th>
              <th className="text-left py-4 px-4 font-semibold text-sm text-gray-700">Fiyat</th>
              <th className="text-left py-4 px-4 font-semibold text-sm text-gray-700">Durum</th>
              <th className="text-left py-4 px-4 font-semibold text-sm text-gray-700">Sorumlu</th>
              <th
                className="text-left py-4 px-4 font-semibold text-sm text-gray-700 cursor-pointer hover:bg-gray-100 select-none"
                onClick={() => toggleSort('updatedAt')}
                title="Son işlem tarihine göre sırala"
              >
                <span className="inline-flex items-center gap-1">
                  Tarih
                  {sortConfig.key === 'updatedAt' && (
                    sortConfig.direction === 'desc'
                      ? <ChevronDown className="w-3 h-3" />
                      : <ChevronUp className="w-3 h-3" />
                  )}
                </span>
              </th>
              <th className="text-center py-4 px-4 font-semibold text-sm text-gray-700">İşlemler</th>
            </tr>
          </thead>
          <tbody className="divide-y">
            {sortedProducts.map(product => {
              const dept = getDepartmentInfo(product.status);
              const categories = [
                product.categoryInfo?.mainCategory,
                product.categoryInfo?.subCategory,
                product.categoryInfo?.detailCategory
              ].filter(Boolean).join(' > ') || '-';
              
              // Son geri yollama notunu bul
              // Eski kayıtlarda action field'ı olmayabilir, bu yüzden status'a da bakalım
              // const lastRejectNote = product.workflowHistory?.filter((h: any) => 
              //   (h.action === 'reject' || h.status === 'rejected') && (h.rejectReason || h.notes)
              // ).pop();
              
              return (
                <React.Fragment key={product._id}>
                  <tr className="hover:bg-gray-50 transition-colors border-b border-gray-100">
                  <td className="py-4 px-4 align-top">
                    <div>
                      <div className="flex items-center gap-2 flex-wrap">
                        <span className="font-medium">
                          {product.productionInfo?.productName || 'İsimsiz Ürün'}
                        </span>
                        {product.isRPT === true && (
                          <Badge variant="secondary" className="text-xs">RPT</Badge>
                        )}
                        {/* Eski format desteği (geriye dönük uyumluluk) */}
                        {!product.productionInfo?.colors?.length && product.productionInfo?.colorName && (
                          <Badge variant="outline" className="text-xs">
                            {product.productionInfo.colorName}
                          </Badge>
                        )}
                        {/* Yeni format: Çoklu renk varyantları */}
                        {product.productionInfo?.colors && product.productionInfo.colors.length > 0 && (
                          <div className="flex flex-wrap gap-1">
                            {product.productionInfo.colors.map((color, idx) => (
                              <Badge key={idx} variant="outline" className="text-xs">
                                {color.code} - {color.name}
                              </Badge>
                            ))}
                          </div>
                        )}
                      </div>
                      <div className="text-sm text-muted-foreground mt-1">
                        {product.productionInfo?.manufacturer || 'Üretici belirtilmemiş'}
                        {product.productionInfo?.purchaseCode && (
                          <span className="ml-2 text-xs">• Satın Alma: {product.productionInfo.purchaseCode}</span>
                        )}
                        {product.productionInfo?.referenceSku && (
                          <span className="ml-2 text-xs">• Ref SKU: {product.productionInfo.referenceSku}</span>
                        )}
                      </div>
                    </div>
                  </td>
                  <td className="py-4 px-4 align-top">
                    {product.nebimInfo ? (
                      <div>
                        <div className="text-sm font-medium">{product.nebimInfo.sku || '-'}</div>
                        <div className="text-xs text-muted-foreground">
                          {product.nebimInfo.colorVariantBarcodes ? 
                            `${Object.keys(product.nebimInfo.colorVariantBarcodes).length} renk varyantı` :
                            product.nebimInfo.variantBarcodes ? 
                              `${Object.keys(product.nebimInfo.variantBarcodes).length} varyant barkodu` : 
                              'Barkod yok'
                          }
                        </div>
                      </div>
                    ) : (
                      <span className="text-muted-foreground">-</span>
                    )}
                    {/* Ticimax ID özet */}
                    {product.ticimaxProductIds && Object.keys(product.ticimaxProductIds).length > 0 ? (
                      <div className="mt-1.5 flex flex-wrap gap-1">
                        {Object.entries(product.ticimaxProductIds).map(([code, id]) => (
                          <span key={code} className="inline-flex items-center text-xs bg-blue-50 text-blue-700 rounded px-1.5 py-0.5 font-mono">
                            {code}:{id}
                          </span>
                        ))}
                      </div>
                    ) : product.ticimaxProductId ? (
                      <div className="mt-1.5">
                        <span className="inline-flex items-center text-xs bg-blue-50 text-blue-700 rounded px-1.5 py-0.5 font-mono">
                          TID:{product.ticimaxProductId}
                        </span>
                      </div>
                    ) : null}
                  </td>
                  <td className="py-4 px-4 align-top">
                    <div className="text-xs text-muted-foreground">{categories}</div>
                  </td>
                  <td className="py-4 px-4 text-sm align-top">
                    {getPriceDisplay(product)}
                  </td>
                  <td className="py-4 px-4 align-top">
                    <Badge variant="outline" className="text-xs">
                      {dept.name}
                    </Badge>
                  </td>
                  <td className="py-4 px-4 text-sm align-top">
                    {dept.person}
                  </td>
                  <td className="py-4 px-4 text-sm text-muted-foreground align-top">
                    {formatDate(product.updatedAt || product.createdAt)}
                  </td>
                  <td className="py-4 px-4 align-top">
                    <div className="flex items-center justify-center gap-2">
                        {/* Production Panel Actions */}
                        {/* Draft durumundaki ürünleri düzenle ve Nebim'e gönder */}
                        {product.status === 'draft' && user?.role === 'production' && (
                          <>
                            {onEdit && (
                              <Button
                                size="sm"
                                variant="outline"
                                onClick={() => onEdit(product)}
                                title="Ürünü düzenle"
                              >
                                <Edit className="h-3 w-3 mr-1" />
                                Düzenle
                              </Button>
                            )}
                            <Button
                              size="sm"
                              variant="default"
                              onClick={async () => {
                                try {
                                  await axios.post(`/products/${product._id}/transition`, {
                                    notes: 'Ürün bilgileri girildi, SKU bekleniyor'
                                  });
                                  toast.success('Ürün Nebim departmanına gönderildi!');
                                  onRefresh();
                                } catch (error) {
                                  toast.error('İşlem başarısız!');
                                }
                              }}
                            >
                              Tamamlandı
                            </Button>
                          </>
                        )}
                        
                        {/* Production - barcode_sent durumundaki ürünleri üreticiye gönder */}
                        {product.status === 'barcode_sent' && user?.role === 'production' && onSentToProducer && (
                          <Button
                            size="sm"
                            variant="default"
                            onClick={() => onSentToProducer(product._id)}
                          >
                            Üreticiye Gönderildi
                          </Button>
                        )}
                        
                        {/* Nebim Panel - nebim_entry durumundaki ürünler */}
                        {product.status === 'nebim_entry' && user?.role === 'nebim' && (
                          <>
                            {onEdit && (
                              <Button
                                size="sm"
                                variant="outline"
                                onClick={() => onEdit(product)}
                                title="Ürünü düzenle"
                              >
                                <Edit className="h-3 w-3 mr-1" />
                                Düzenle
                              </Button>
                            )}
                            {/* Tamamlandı butonu - SKU ve barkod kontrolü (geri yollama durumunda eksik olabilir) */}
                            {onSentToProducer && product.nebimInfo?.sku && (
                              (product.nebimInfo?.variantBarcodes && Object.keys(product.nebimInfo.variantBarcodes).length > 0) ||
                              (product.nebimInfo?.colorVariantBarcodes && Object.keys(product.nebimInfo.colorVariantBarcodes).length > 0)
                            ) ? (
                              <Button
                                size="sm"
                                variant="default"
                                onClick={() => onSentToProducer(product._id)}
                              >
                                Tamamlandı
                              </Button>
                            ) : onSentToProducer ? (
                              <Button
                                size="sm"
                                variant="secondary"
                                disabled
                                title="SKU ve varyant barkodları girilmeden üretime gönderilemez"
                              >
                                SKU/Barkod Eksik
                              </Button>
                            ) : null}
                          </>
                        )}
                        
                        {/* Reject Button - Sadece yetkili kullanıcılar görebilir */}
                        {product.status !== 'draft' && onReject && canRejectProduct(product) && (
                          <Button
                            size="sm"
                            variant="outline"
                            className="text-red-600 hover:text-red-700 hover:bg-red-50"
                            onClick={() => setRejectModal({ show: true, product })}
                            title="Bu aşamadan ürünü geri yollayabilirsiniz"
                          >
                            <ArrowLeft className="h-3 w-3 mr-1" />
                            Geri Yolla
                          </Button>
                        )}

                        {/* Kapatma Talebi Butonu — sadece canlıda ve açık olan ürünlerde */}
                        {onClosureRequest && product.status === 'published' && product.isActive !== false && product.closureRequest?.status !== 'pending' && (
                          <Button
                            size="sm"
                            variant="outline"
                            className="text-orange-600 hover:text-orange-700 hover:bg-orange-50"
                            onClick={() => setClosureModal({ show: true, product })}
                            title="Ürün kartı kapatma talebi gönder"
                          >
                            <Ban className="h-3 w-3 mr-1" />
                            Kapatma Talebi
                          </Button>
                        )}
                        {product.status === 'published' && product.isActive !== false && product.closureRequest?.status === 'pending' && (
                          <Badge variant="outline" className="text-orange-600 border-orange-300 text-xs">
                            Kapatma Bekliyor
                          </Badge>
                        )}
                        {product.status === 'published' && product.isActive === false && (
                          <Badge variant="outline" className="text-gray-600 border-gray-300 text-xs">
                            Kapalı
                          </Badge>
                        )}
                        
                        
                        {/* NEBIM: SKU İşlemleri - production_entry durumunda */}
                        {product.status === 'production_entry' && user?.role === 'nebim' && (
                          <>
                            {onEdit && (
                              <Button
                                size="sm"
                                variant="outline"
                                onClick={() => onEdit(product)}
                                title="Ürünü düzenle"
                              >
                                <Edit className="h-3 w-3 mr-1" />
                                Düzenle
                              </Button>
                            )}
                            {/* Tamamlandı butonu sadece SKU ve varyant barkodları girildiyse görünsün */}
                            {/* Hem eski format (variantBarcodes) hem yeni format (colorVariantBarcodes) desteklenir */}
                            {product.nebimInfo?.sku && (
                              (product.nebimInfo?.variantBarcodes && Object.keys(product.nebimInfo.variantBarcodes).length > 0) ||
                              (product.nebimInfo?.colorVariantBarcodes && Object.keys(product.nebimInfo.colorVariantBarcodes).length > 0)
                            ) ? (
                              <Button
                                size="sm"
                                variant="default"
                                onClick={async () => {
                                  try {
                                    await axios.post(`/products/${product._id}/transition`, {
                                      notes: 'SKU ve barkod bilgileri girildi'
                                    });
                                    toast.success('Ürün bir sonraki aşamaya gönderildi!');
                                    onRefresh();
                                  } catch (error) {
                                    toast.error('İşlem başarısız!');
                                  }
                                }}
                              >
                                Tamamlandı
                              </Button>
                            ) : (
                              <Button
                                size="sm"
                                variant="secondary"
                                disabled
                                title="SKU ve varyant barkodları girilmeden tamamlanamaz"
                              >
                                SKU/Barkod Bekliyor
                              </Button>
                            )}
                          </>
                        )}
                        
                        {/* WAREHOUSE: Stok Girişi */}
                        {product.status === 'stock_entry' && user?.role === 'warehouse' && (
                          <Button
                            size="sm"
                            variant="default"
                            onClick={async () => {
                              try {
                                await axios.post(`/products/${product._id}/transition`, {
                                  notes: 'Ürün depoda teslim alındı'
                                });
                                toast.success('Ürün teslim alındı ve bir sonraki aşamaya gönderildi!');
                                onRefresh();
                              } catch (error) {
                                toast.error('İşlem başarısız!');
                              }
                            }}
                          >
                            Tamamlandı
                          </Button>
                        )}

                        {/* SADECE ADMIN: Ticimaxa Aktarımı - stock_entry aşamasında */}
                        {product.status === 'stock_entry' && user?.role === 'admin' && (
                          <Button
                            size="sm"
                            variant="default"
                            className="bg-blue-600 hover:bg-blue-700"
                            onClick={async () => {
                              if (onErpTransfer) {
                                onErpTransfer(product._id);
                                return;
                              }

                              try {
                                await axios.post(`/products/${product._id}/transition`, {
                                  notes: 'Ürün ERP sistemine aktarıldı'
                                });
                                toast.success('Ürün ERP aktarım aşamasına gönderildi!');
                                onRefresh();
                              } catch (error) {
                                toast.error('İşlem başarısız!');
                              }
                            }}
                          >
                            ERP'ye Aktar
                          </Button>
                        )}

                        {/* API Transfer durumundaki ürünler için Ticimax ID girişi */}
                        {product.status === 'api_transfer' && (user?.role === 'admin' || user?.role === 'erp') && (
                          <div className="flex gap-2">
                            {/* Ticimax ID Gir/Değiştir Butonu - Admin veya ERP */}
                            <Button
                              size="sm"
                              variant="outline"
                              className="border-blue-600 text-blue-600 hover:bg-blue-50"
                              onClick={() => onErpTransfer ? onErpTransfer(product._id) : openTicimaxEditModal(product)}
                            >
                              <Edit className="w-3 h-3 mr-1" />
                              {hasValidTicimaxId(product) ? 'Ticimax ID Düzenle' : 'Ticimax ID Gir'}
                            </Button>
                            
                            {/* Ticimax ID varsa göster */}
                            {hasValidTicimaxId(product) && (
                              <span className="text-sm text-green-600 font-medium flex items-center">
                                ✓ ID: {getValidTicimaxIds(product)[0]}
                              </span>
                            )}
                            
                            {/* Aktarım Yapıldı Butonu - Sadece ID varsa aktif */}
                            <Button
                              size="sm"
                              variant="default"
                              disabled={!hasValidTicimaxId(product)}
                              title={
                                !hasValidTicimaxId(product)
                                  ? 'Önce "Ticimax ID Gir" butonuyla ID girmelisiniz'
                                  : 'Ürünü içerik girişi aşamasına aktar'
                              }
                              onClick={async () => {
                                try {
                                  await axios.post(`/products/${product._id}/transition`, {
                                    notes: 'Ürün ERP sistemine başarıyla aktarıldı'
                                  });
                                  toast.success('Ürün içerik girişine yönlendirildi!');
                                  onRefresh();
                                } catch (error) {
                                  toast.error('İşlem başarısız!');
                                }
                              }}
                              className={hasValidTicimaxId(product) ? '' : 'opacity-50 cursor-not-allowed'}
                            >
                              Aktarım Yapıldı
                            </Button>
                          </div>
                        )}

                        {/* CONTENT: İçerik girişi */}
                        {product.status === 'api_transfer' && user?.role === 'content' && onContentAction && (
                          <Button
                            size="sm"
                            variant="default"
                            onClick={() => onContentAction(product._id)}
                          >
                            İçerik Gir
                          </Button>
                        )}

                        {/* CONTENT: content_entry durumunda - kategorileme adımına gönder */}
                        {product.status === 'content_entry' && (user?.role === 'content' || user?.role === 'admin') && (
                          <>
                            {onEdit && (
                              <Button
                                size="sm"
                                variant="outline"
                                onClick={() => onEdit(product)}
                                title="İçerik bilgilerini düzenle"
                              >
                                <Edit className="h-3 w-3 mr-1" />
                                Düzenle
                              </Button>
                            )}
                            {/* İçerik (ön yazı + açıklama) tamamlandıysa kategorileme aşamasına gönder */}
                            {product.contentInfo?.shortDescription &&
                             product.contentInfo?.longDescription ? (
                              <Button
                                size="sm"
                                variant="default"
                                onClick={async () => {
                                  try {
                                    await axios.post(`/products/${product._id}/transition`, {
                                      notes: 'İçerik bilgileri tamamlandı, kategorileme aşamasına gönderildi'
                                    });
                                    toast.success('Ürün kategorileme aşamasına gönderildi!');
                                    onRefresh();
                                  } catch (error) {
                                    toast.error('İşlem başarısız!');
                                  }
                                }}
                              >
                                Kategorilemeye Gönder
                              </Button>
                            ) : (
                              <Button
                                size="sm"
                                variant="secondary"
                                disabled
                                title="İçerik (ön yazı ve açıklama) girilmeden tamamlanamaz"
                              >
                                İçerik Bekliyor
                              </Button>
                            )}
                          </>
                        )}

                        {/* CONTENT: category_setup durumunda - site kategori bilgileri tamamlanır */}
                        {product.status === 'category_setup' && (user?.role === 'content' || user?.role === 'admin') && (
                          <>
                            {onEdit && (
                              <Button
                                size="sm"
                                variant="outline"
                                onClick={() => onEdit(product)}
                                title="Kategori bilgilerini düzenle"
                              >
                                <Edit className="h-3 w-3 mr-1" />
                                Düzenle
                              </Button>
                            )}
                            {product.categoryInfo?.siteMainCategory ? (
                              <Button
                                size="sm"
                                variant="default"
                                onClick={async () => {
                                  try {
                                    await axios.post(`/products/${product._id}/transition`, {
                                      notes: 'Site kategori bilgileri tamamlandı, görsel aşamasına gönderildi'
                                    });
                                    toast.success('Ürün görsel aşamasına gönderildi!');
                                    onRefresh();
                                  } catch (error: any) {
                                    toast.error(error.response?.data?.message || 'İşlem başarısız!');
                                  }
                                }}
                              >
                                Görsele Gönder
                              </Button>
                            ) : (
                              <Button
                                size="sm"
                                variant="secondary"
                                disabled
                                title="Site ana kategori seçilmeden görsel aşamasına gönderilemez"
                              >
                                Kategori Bekliyor
                              </Button>
                            )}
                          </>
                        )}

                        {/* VISUAL: Görsel yükleme - image_upload durumunda */}
                        {product.status === 'image_upload' && user?.role === 'visual' && onImageUpload && (
                          <>
                            {onEdit && (
                              <Button
                                size="sm"
                                variant="outline"
                                onClick={() => onEdit(product)}
                                title="Görsel bilgilerini düzenle"
                              >
                                <Edit className="h-3 w-3 mr-1" />
                                Düzenle
                              </Button>
                            )}
                            <Button
                              size="sm"
                              variant="default"
                              onClick={() => onImageUpload(product._id)}
                            >
                              Tamamlandı
                            </Button>
                          </>
                        )}

                        {/* PUBLISH, ADMIN veya ERP: Yayınlama - publish_pending veya legacy image_upload+uploadedExternally */}
                        {/* TODO: migration sonrası image_upload+uploadedExternally OR koşulu kaldırılacak */}
                        {(product.status === 'publish_pending' ||
                          (product.status === 'image_upload' && product.visualInfo?.uploadedExternally === true)) &&
                          (user?.role === 'publish' || user?.role === 'admin' || user?.role === 'erp') && onPublish && (
                          <Button
                            size="sm"
                            variant="default"
                            className="bg-green-600 hover:bg-green-700"
                            onClick={() => onPublish(product._id)}
                          >
                            Yayına Al
                          </Button>
                        )}
                        
                        {/* Admin Only: Force Status Change Button */}
                        {user?.role === 'admin' && (
                          <Button
                            size="sm"
                            variant="outline"
                            className="border-amber-500 text-amber-600 hover:bg-amber-50"
                            onClick={() => setForceStatusModal({ show: true, product })}
                            title="Adım Değiştir (ADMIN)"
                          >
                            <Settings2 className="w-4 h-4" />
                          </Button>
                        )}
                        
                        {/* Admin Only: Delete Button */}
                        {user?.role === 'admin' && onDelete && (
                          <Button
                            size="sm"
                            variant="destructive"
                            onClick={() => onDelete(product)}
                            title="Ürünü Sil (ADMIN)"
                          >
                            <Trash2 className="w-4 h-4" />
                          </Button>
                        )}

                        {/* Expand Button for Details */}
                        <Button
                          variant="ghost"
                          size="sm"
                          onClick={() => toggleRowExpansion(product._id)}
                        >
                          {expandedRows.has(product._id) ? (
                            <ChevronUp className="w-4 h-4" />
                          ) : (
                            <ChevronDown className="w-4 h-4" />
                          )}
                        </Button>
                        
                        {/* Default state - Hiçbir işlem yoksa */}
                        {!canRejectProduct(product) && 
                          product.status !== 'production_entry' &&
                          product.status !== 'content_entry' &&
                          product.status !== 'category_setup' &&
                          product.status !== 'stock_entry' &&
                          product.status !== 'image_upload' && (
                          <span className="text-muted-foreground text-sm">Takip</span>
                        )}
                    </div>
                  </td>
                </tr>
                {expandedRows.has(product._id) && (
                  <tr>
                    <td colSpan={8} className="bg-muted/30 px-8 py-6">
                      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
                        {/* Barkod Bilgileri - Eski Format (Tek Renk) */}
                        {product.nebimInfo?.variantBarcodes &&
                          (!product.nebimInfo?.colorVariantBarcodes ||
                            Object.keys(product.nebimInfo.colorVariantBarcodes).length === 0) && (
                          <div className="space-y-3">
                            <div className="flex items-center gap-2 font-medium">
                              <Barcode className="w-4 h-4" />
                              Varyant Barkodları:
                            </div>
                            <div className="grid grid-cols-2 gap-2">
                              {sortSizeEntries(product.nebimInfo.variantBarcodes).map(([size, barcode]) => (
                                <div key={size} className="flex items-center gap-2 bg-background rounded-md p-2">
                                  <Badge variant="outline">{size}</Badge>
                                  <span className="text-sm font-mono">{barcode}</span>
                                </div>
                              ))}
                            </div>
                          </div>
                        )}

                        {/* Barkod Bilgileri - Yeni Format (Çoklu Renk) */}
                        {product.nebimInfo?.colorVariantBarcodes &&
                          Object.keys(product.nebimInfo.colorVariantBarcodes).length > 0 && (
                          <div className="space-y-3 lg:col-span-2">
                            <div className="flex items-center gap-2 font-medium">
                              <Barcode className="w-4 h-4" />
                              Renk ve Beden Varyantları:
                            </div>
                            <div className="space-y-3">
                              {Object.entries(product.nebimInfo.colorVariantBarcodes).map(([colorCode, sizeBarcodesMap]) => {
                                const colorVariant = product.productionInfo?.colors?.find(c => c.code === colorCode);
                                return (
                                  <div key={colorCode} className="bg-background rounded-md p-3">
                                    <div className="flex items-center gap-2 mb-2">
                                      <Badge variant="secondary" className="text-xs">
                                        {colorCode} - {colorVariant?.name || 'Bilinmeyen Renk'}
                                      </Badge>
                                    </div>
                                    <div className="grid grid-cols-2 md:grid-cols-3 gap-2">
                                      {sortSizeEntries(sizeBarcodesMap).map(([size, barcode]) => (
                                        <div key={size} className="flex items-center gap-2 bg-muted/30 rounded-md p-2">
                                          <Badge variant="outline" className="text-xs">{size}</Badge>
                                          <span className="text-xs font-mono">{barcode}</span>
                                        </div>
                                      ))}
                                    </div>
                                  </div>
                                );
                              })}
                            </div>
                          </div>
                        )}

                        {/* Renk Varyantları */}
                        {product.productionInfo?.colors && product.productionInfo.colors.length > 0 && (
                          <div className="space-y-3">
                            <div className="flex items-center gap-2 font-medium">
                              <Palette className="w-4 h-4" />
                              Renk Varyantları:
                            </div>
                            <div className="flex flex-wrap gap-2">
                              {product.productionInfo.colors.map((color, idx) => (
                                <Badge key={idx} variant="secondary" className="text-xs">
                                  <span className="font-mono mr-1">{color.code}</span>
                                  <span>{color.name}</span>
                                </Badge>
                              ))}
                            </div>
                          </div>
                        )}

                        {/* Ticimax ID Bilgileri */}
                        {(product.ticimaxProductId || (product.ticimaxProductIds && Object.keys(product.ticimaxProductIds).length > 0)) && (
                          <div className="space-y-3">
                            <div className="flex items-center gap-2 font-medium">
                              <Settings2 className="w-4 h-4" />
                              Ticimax ID'leri:
                            </div>
                            <div className="bg-background rounded-md p-3 space-y-2">
                              {/* Çoklu renk ID'leri */}
                              {product.ticimaxProductIds && Object.keys(product.ticimaxProductIds).length > 0 ? (
                                <div className="space-y-1.5">
                                  {Object.entries(product.ticimaxProductIds).map(([colorCode, id]) => {
                                    const colorName = product.productionInfo?.colors?.find(c => c.code === colorCode)?.name || colorCode;
                                    return (
                                      <div key={colorCode} className="flex items-center justify-between bg-muted/30 rounded-md px-3 py-1.5">
                                        <div className="flex items-center gap-2">
                                          <Badge variant="outline" className="text-xs">{colorCode}</Badge>
                                          <span className="text-sm text-muted-foreground">{colorName}</span>
                                        </div>
                                        <span className="text-sm font-mono font-medium">{id}</span>
                                      </div>
                                    );
                                  })}
                                </div>
                              ) : product.ticimaxProductId ? (
                                <div className="flex items-center justify-between">
                                  <span className="text-sm text-muted-foreground">Ürün ID:</span>
                                  <span className="text-sm font-mono font-medium">{product.ticimaxProductId}</span>
                                </div>
                              ) : null}

                              {/* Admin: Düzenle / Sil butonları */}
                              {user?.role === 'admin' && (
                                <div className="flex gap-2 pt-2 border-t mt-2">
                                  <Button
                                    size="sm"
                                    variant="outline"
                                    className="text-blue-600 border-blue-300 hover:bg-blue-50"
                                    onClick={() => openTicimaxEditModal(product)}
                                  >
                                    <Edit className="w-3 h-3 mr-1" />
                                    Düzenle
                                  </Button>
                                  <Button
                                    size="sm"
                                    variant="outline"
                                    className="text-red-600 border-red-300 hover:bg-red-50"
                                    onClick={async () => {
                                      if (!window.confirm('Ticimax ID\'leri silmek istediğinizden emin misiniz?')) return;
                                      try {
                                        await axios.delete(`/products/${product._id}/ticimax-ids`);
                                        toast.success('Ticimax ID\'leri silindi!');
                                        onRefresh();
                                      } catch (error) {
                                        toast.error('Silme işlemi başarısız!');
                                      }
                                    }}
                                  >
                                    <Trash2 className="w-3 h-3 mr-1" />
                                    Sil
                                  </Button>
                                </div>
                              )}
                            </div>
                          </div>
                        )}

                        {/* Beden Dağılımı */}
                        {product.productionInfo?.sizeDistribution && (
                          <div className="space-y-3">
                            <div className="flex items-center gap-2 font-medium">
                              <Package className="w-4 h-4" />
                              Beden Dağılımı:
                            </div>
                            <div className="grid grid-cols-2 gap-2">
                              {sortSizeEntries(product.productionInfo.sizeDistribution).map(([size, quantity]) => (
                                <div key={size} className="flex items-center gap-2 bg-background rounded-md p-2">
                                  <Badge variant="outline">{size}</Badge>
                                  <span className="text-sm">{quantity} adet</span>
                                </div>
                              ))}
                            </div>
                          </div>
                        )}

                        {/* Kumaş Teknik Bilgileri */}
                        {product.productionInfo?.fabricInfo && (
                          <div className="space-y-3">
                            <div className="flex items-center gap-2 font-medium">
                              <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5H7a2 2 0 00-2 2v12a2 2 0 002 2h10a2 2 0 002-2V7a2 2 0 00-2-2h-2M9 5a2 2 0 002 2h2a2 2 0 002-2M9 5a2 2 0 012-2h2a2 2 0 012 2" />
                              </svg>
                              Kumaş Teknik Bilgiler:
                            </div>
                            <div className="bg-background rounded-md p-3">
                              <p className="text-sm whitespace-pre-wrap">{product.productionInfo.fabricInfo}</p>
                            </div>
                          </div>
                        )}

                        {/* Sevk Bilgileri */}
                        {(product.productionInfo?.shippingQuantities || product.productionInfo?.shippingDate) && (
                          <div className="space-y-3">
                            <div className="flex items-center gap-2 font-medium">
                              <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 7H5a2 2 0 00-2 2v9a2 2 0 002 2h14a2 2 0 002-2V9a2 2 0 00-2-2h-3m-1 4l-3 3m0 0l-3-3m3 3V2" />
                              </svg>
                              Sevk Bilgileri:
                            </div>
                            <div className="bg-background rounded-md p-3 space-y-2">
                              {product.productionInfo.shippingQuantities && (
                                <div className="space-y-2">
                                  <div className="flex items-center justify-between">
                                    <span className="text-sm text-muted-foreground">Sevk Edilecek Adetler:</span>
                                    <Badge variant="secondary">
                                      {Object.values(product.productionInfo.shippingQuantities as Record<string, number>).reduce((sum, qty) => sum + qty, 0)} adet
                                    </Badge>
                                  </div>
                                  <div className="grid grid-cols-3 gap-1 text-xs">
                                    {sortSizeEntries(product.productionInfo.shippingQuantities as Record<string, number>)
                                      .filter(([_, qty]) => qty > 0)
                                      .map(([size, qty]) => (
                                        <div key={size} className="flex items-center gap-1">
                                          <Badge variant="outline" className="text-xs px-1 py-0">{size}</Badge>
                                          <span className="text-muted-foreground">{qty}</span>
                                        </div>
                                      ))
                                    }
                                  </div>
                                </div>
                              )}
                              {product.productionInfo.shippingDate && (
                                <div className="flex items-center justify-between">
                                  <span className="text-sm text-muted-foreground">Sevk Tarihi:</span>
                                  <Badge variant="outline">
                                    {new Date(product.productionInfo.shippingDate).toLocaleDateString('tr-TR', { 
                                      day: '2-digit',
                                      month: 'long',
                                      year: 'numeric'
                                    })}
                                  </Badge>
                                </div>
                              )}
                            </div>
                          </div>
                        )}

                        {/* Alakasyon Dağılımı (Beden Bazlı) */}
                        {product.productionInfo?.allocationQuantities && Object.keys(product.productionInfo.allocationQuantities).length > 0 && (
                          <div className="space-y-3">
                            <div className="flex items-center gap-2 font-medium">
                              <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 20h5v-2a3 3 0 00-5.356-1.857M17 20H7m10 0v-2c0-.656-.126-1.283-.356-1.857M7 20H2v-2a3 3 0 015.356-1.857M7 20v-2c0-.656.126-1.283.356-1.857m0 0a5.002 5.002 0 019.288 0M15 7a3 3 0 11-6 0 3 3 0 016 0zm6 3a2 2 0 11-4 0 2 2 0 014 0zM7 10a2 2 0 11-4 0 2 2 0 014 0z" />
                              </svg>
                              Alakasyon Dağılımı (Beden Bazlı):
                            </div>
                            <div className="space-y-2">
                              {Object.entries(product.productionInfo.allocationQuantities).map(([customer, allocation]) => {
                                const sizes = allocation.sizes || allocation;
                                const shippingDate = allocation.shippingDate;
                                const totalQty = typeof sizes === 'object' 
                                  ? Object.values(sizes as Record<string, number>).reduce((sum, qty) => sum + qty, 0)
                                  : 0;
                                
                                return (
                                  <div key={customer} className="bg-background rounded-md p-3">
                                    <div className="flex items-center justify-between mb-2">
                                      <div>
                                        <span className="text-sm font-medium">{customer}</span>
                                        {shippingDate && (
                                          <span className="text-xs text-muted-foreground ml-2">
                                            Sevk: {new Date(shippingDate).toLocaleDateString('tr-TR')}
                                          </span>
                                        )}
                                      </div>
                                      <Badge variant="secondary">{totalQty} adet</Badge>
                                    </div>
                                    {typeof sizes === 'object' && (
                                      <div className="grid grid-cols-3 gap-1 text-xs">
                                        {sortSizeEntries(sizes as Record<string, number>)
                                          .filter(([_, qty]) => qty > 0)
                                          .map(([size, qty]) => (
                                            <div key={size} className="flex items-center gap-1">
                                              <Badge variant="outline" className="text-xs px-1 py-0">{size}</Badge>
                                              <span className="text-muted-foreground">{qty}</span>
                                            </div>
                                          ))
                                        }
                                      </div>
                                    )}
                                  </div>
                                );
                              })}
                            </div>
                          </div>
                        )}

                        {/* İş Akışı Geçmişi */}
                        {product.workflowHistory && product.workflowHistory.length > 0 && (
                          <div className="space-y-3 lg:col-span-1">
                            <div className="flex items-center gap-2 font-medium text-sm">
                              <History className="w-4 h-4 text-primary" />
                              <span>İşlem Geçmişi</span>
                            </div>
                            <div className="relative">
                              <div className="absolute left-4 top-0 bottom-0 w-0.5 bg-border"></div>
                              <div className="space-y-4">
                                {product.workflowHistory
                                  .filter(step => {
                                    // Sadece departman geçişlerini, geri yollamaları ve kapatma taleplerini göster
                                    const isTransition = step.stepName?.includes('->') || step.stepName === 'Ürün oluşturuldu';
                                    const isRejection = step.status === 'rejected' || step.action === 'reject';
                                    const isClosureAction = step.action === 'closure_request' || step.action === 'closure_approved' || step.action === 'closure_rejected' || step.action === 'closure_reopened';
                                    return isTransition || isRejection || isClosureAction;
                                  })
                                  .sort((a, b) => {
                                    const dateA = new Date(a.completedAt || 0).getTime();
                                    const dateB = new Date(b.completedAt || 0).getTime();
                                    return dateA - dateB; // Eskiden yeniye doğru sırala
                                  })
                                  .map((step, index, array) => {
                                    const isLast = index === array.length - 1;
                                    const stepDate = step.completedAt ? new Date(step.completedAt) : null;
                                    
                                    // Kullanıcı ismini al
                                    const getUserName = () => {
                                      if (!step.completedBy) return 'Sistem';
                                      
                                      // Obje olarak geldiyse (populate edilmiş)
                                      if (typeof step.completedBy === 'object') {
                                        const user = step.completedBy as any;
                                        return user.name || user.email || getUserDisplayName(user._id);
                                      }
                                      
                                      // String olarak geldiyse (sadece ID)
                                      return getUserDisplayName(step.completedBy);
                                    };
                                    const userName = getUserName();
                                    
                                    // Step isimlerini Türkçeleştir
                                    const getStepDisplayName = (stepName: string) => {
                                      // Departman geçişlerini güzel göster
                                      const stepNames: Record<string, string> = {
                                        'Ürün oluşturuldu': 'Ürün Oluşturuldu',
                                        'draft -> production_entry': 'Nebim Departmanına Gönderildi',
                                        'production_entry -> nebim_entry': 'SKU ve Barkod Tanımlandı',
                                        'nebim_entry -> barcode_sent': 'Üretime Gönderildi',
                                        'barcode_sent -> stock_entry': 'Depoya Alındı',
                                        'stock_entry -> api_transfer': 'ERP\'ye Aktarıldı',
                                        'api_transfer -> content_entry': 'İçerik Girişine Gönderildi',
                                        'content_entry -> category_setup': 'Kategorileme İşlemine Alındı',
                                        'category_setup -> image_upload': 'Görsel Yüklemeye Gönderildi',
                                        'image_upload -> publish_pending': 'Görsel Tamamlandı, Yayın Onayı Bekleniyor',
                                        'publish_pending -> published': 'Yayına Alındı',
                                        'image_upload -> published': 'Yayınlanan Ürünler (eski akış)'
                                      };
                                      
                                      // Geri yollama adımlarını özel işle
                                      if (stepName?.includes('(Geri Yollama)')) {
                                        return 'Geri Yollandı';
                                      }
                                      
                                      return stepNames[stepName] || stepName;
                                    };
                                    
                                    // Geri yollama adımı için özel tasarım
                                    if (step.status === 'rejected' || step.action === 'reject') {
                                      return (
                                        <div key={index} className="relative flex gap-3">
                                          <div className="relative z-10 flex h-8 w-8 items-center justify-center rounded-full bg-red-100 border-2 border-red-400">
                                            <ArrowLeft className="w-4 h-4 text-red-600" />
                                          </div>
                                          <div className="flex-1 pb-4">
                                            <div className="rounded-lg border border-red-200 bg-red-50 p-3">
                                              <div className="flex items-start justify-between gap-2">
                                                <div className="flex-1">
                                                  <p className="text-sm font-medium text-red-900">
                                                    Geri Yollandı
                                                  </p>
                                                  <p className="text-xs text-red-700 mt-1">
                                                    <span className="font-medium">Not:</span> "{step.rejectReason || step.notes || 'Not belirtilmemiş'}"
                                                  </p>
                                                  {step.returnedTo && (
                                                    <p className="text-xs text-red-600 mt-1">
                                                      <span className="font-medium">Hedef:</span> {STEP_NAMES[step.returnedTo] || step.returnedTo}
                                                    </p>
                                                  )}
                                                </div>
                                                {stepDate && (
                                                  <Badge variant="destructive" className="text-xs shrink-0">
                                                    {stepDate.toLocaleString('tr-TR', {
                                                      day: '2-digit',
                                                      month: '2-digit',
                                                      year: 'numeric',
                                                      hour: '2-digit',
                                                      minute: '2-digit'
                                                    })}
                                                  </Badge>
                                                )}
                                              </div>
                                              <div className="mt-2 flex items-center gap-2">
                                                <User className="w-3 h-3 text-red-600" />
                                                <span className="text-xs text-red-600 font-medium">
                                                  {userName}
                                                </span>
                                              </div>
                                            </div>
                                          </div>
                                        </div>
                                      );
                                    }
                                    
                                    // Kapatma talebi adımları için özel tasarım
                                    if (step.action === 'closure_request' || step.action === 'closure_approved' || step.action === 'closure_rejected' || step.action === 'closure_reopened') {
                                      const isRequest = step.action === 'closure_request';
                                      const isApproved = step.action === 'closure_approved' || step.action === 'closure_reopened';
                                      const bgColor = isRequest ? 'bg-orange-100 border-orange-400' : isApproved ? 'bg-green-100 border-green-400' : 'bg-red-100 border-red-400';
                                      const textColor = isRequest ? 'text-orange-600' : isApproved ? 'text-green-600' : 'text-red-600';
                                      const borderColor = isRequest ? 'border-orange-200 bg-orange-50' : isApproved ? 'border-green-200 bg-green-50' : 'border-red-200 bg-red-50';
                                      const titleColor = isRequest ? 'text-orange-900' : isApproved ? 'text-green-900' : 'text-red-900';
                                      const noteColor = isRequest ? 'text-orange-700' : isApproved ? 'text-green-700' : 'text-red-700';

                                      return (
                                        <div key={index} className="relative flex gap-3">
                                          <div className={`relative z-10 flex h-8 w-8 items-center justify-center rounded-full ${bgColor} border-2`}>
                                            <Ban className={`w-4 h-4 ${textColor}`} />
                                          </div>
                                          <div className="flex-1 pb-4">
                                            <div className={`rounded-lg border ${borderColor} p-3`}>
                                              <div className="flex items-start justify-between gap-2">
                                                <div className="flex-1">
                                                  <p className={`text-sm font-medium ${titleColor}`}>
                                                    {step.stepName || 'Kapatma İşlemi'}
                                                  </p>
                                                  {step.notes && (
                                                    <p className={`text-xs ${noteColor} mt-1`}>
                                                      <span className="font-medium">Not:</span> "{step.notes}"
                                                    </p>
                                                  )}
                                                </div>
                                                {stepDate && (
                                                  <Badge variant="outline" className={`text-xs shrink-0 ${textColor}`}>
                                                    {stepDate.toLocaleString('tr-TR', {
                                                      day: '2-digit',
                                                      month: '2-digit',
                                                      year: 'numeric',
                                                      hour: '2-digit',
                                                      minute: '2-digit'
                                                    })}
                                                  </Badge>
                                                )}
                                              </div>
                                              <div className="mt-2 flex items-center gap-2">
                                                <User className={`w-3 h-3 ${textColor}`} />
                                                <span className={`text-xs ${textColor} font-medium`}>
                                                  {userName}
                                                </span>
                                              </div>
                                            </div>
                                          </div>
                                        </div>
                                      );
                                    }

                                    // Normal adımlar için mevcut tasarım
                                    return (
                                      <div key={index} className="relative flex gap-3">
                                        <div className={`relative z-10 flex h-8 w-8 items-center justify-center rounded-full ${
                                          isLast && step.status === 'completed' ? 'bg-primary text-primary-foreground' : 'bg-muted'
                                        }`}>
                                          {isLast && step.status === 'completed' ? (
                                            <CheckCircle className="w-4 h-4" />
                                          ) : (
                                            <Clock className="w-4 h-4" />
                                          )}
                                        </div>
                                        <div className={`flex-1 ${isLast ? '' : 'pb-4'}`}>
                                          <div className="rounded-lg border bg-card p-3">
                                            <div className="flex items-start justify-between gap-2">
                                              <div className="flex-1">
                                                <p className="text-sm font-medium">
                                                  {getStepDisplayName(step.stepName || 'İşlem')}
                                                </p>
                                              </div>
                                              {stepDate && (
                                                <Badge variant="outline" className="text-xs shrink-0">
                                                  {stepDate.toLocaleString('tr-TR', {
                                                    day: '2-digit',
                                                    month: '2-digit',
                                                    year: 'numeric',
                                                    hour: '2-digit',
                                                    minute: '2-digit'
                                                  })}
                                                </Badge>
                                              )}
                                            </div>
                                            <div className="mt-2 flex items-center gap-2">
                                              <User className="w-3 h-3 text-muted-foreground" />
                                              <span className="text-xs text-muted-foreground">
                                                {userName}
                                              </span>
                                            </div>
                                          </div>
                                        </div>
                                      </div>
                                    );
                                  })}
                              </div>
                            </div>
                          </div>
                        )}
                      </div>
                    </td>
                  </tr>
                )}
                </React.Fragment>
              );
            })}
          </tbody>
        </table>
      </div>
      
      {/* Reject Modal */}
      {rejectModal.show && rejectModal.product && onReject && (
        <RejectModal
          productName={rejectModal.product.productionInfo?.productName || 'Ürün'}
          currentStep={rejectModal.product.status}
          previousSteps={getPreviousSteps(rejectModal.product.status)}
          onClose={() => setRejectModal({ show: false, product: null })}
          onReject={async (targetStep, reason) => {
            if (!rejectModal.product) return;
            await onReject(rejectModal.product._id, targetStep, reason);
            setRejectModal({ show: false, product: null });
            onRefresh();
          }}
        />
      )}

      {/* Force Status Modal (Admin Only) */}
      {forceStatusModal.show && forceStatusModal.product && (
        <ForceStatusModal
          productId={forceStatusModal.product._id}
          productName={forceStatusModal.product.productionInfo?.productName || 'Ürün'}
          currentStatus={forceStatusModal.product.status}
          onClose={() => setForceStatusModal({ show: false, product: null })}
          onConfirm={async (targetStatus, notes) => {
            if (!forceStatusModal.product) {
              toast.error('Ürün bulunamadı!');
              setForceStatusModal({ show: false, product: null });
              return;
            }
            try {
              await axios.post(`/products/${forceStatusModal.product._id}/force-status`, {
                targetStatus,
                notes
              });
              const statusName = STEP_NAMES[targetStatus] || targetStatus;
              toast.success(`Ürün başarıyla "${statusName}" adımına atandı!`);
              setForceStatusModal({ show: false, product: null });
              onRefresh();
            } catch (error: any) {
              if (error.response?.status === 404) {
                toast.error('Ürün bulunamadı! Sayfa yenileniyor...');
                setForceStatusModal({ show: false, product: null });
                onRefresh();
                return;
              }
              toast.error(error.response?.data?.message || 'İşlem başarısız!');
              throw error;
            }
          }}
        />
      )}

      {/* Closure Request Modal */}
      {closureModal.show && closureModal.product && onClosureRequest && (
        <ClosureRequestModal
          productName={closureModal.product.productionInfo?.productName || 'Ürün'}
          onClose={() => setClosureModal({ show: false, product: null })}
          onSubmit={async (reason) => {
            if (!closureModal.product) return;
            await onClosureRequest(closureModal.product._id, reason);
            setClosureModal({ show: false, product: null });
            onRefresh();
          }}
        />
      )}

      {/* Ticimax ID Düzenleme Modalı (Admin Only) */}
      {ticimaxEditModal.show && ticimaxEditModal.product && (
        <div className="fixed inset-0 bg-black/60 backdrop-blur-sm z-50 flex items-center justify-center p-4">
          <div className="bg-white rounded-lg shadow-2xl w-full max-w-2xl">
            <div className="flex items-center justify-between p-6 border-b">
              <div>
                <h2 className="text-xl font-semibold">Ticimax ID Düzenle</h2>
                <p className="text-sm text-gray-600 mt-1">{ticimaxEditModal.product.productionInfo?.productName}</p>
              </div>
              <Button
                variant="ghost"
                size="sm"
                onClick={() => setTicimaxEditModal({ show: false, product: null })}
              >
                <X className="w-4 h-4" />
              </Button>
            </div>

            <div className="p-6 space-y-6">
              {ticimaxEditModal.product.productionInfo?.colors && ticimaxEditModal.product.productionInfo.colors.length > 0 ? (
                <div className="space-y-4">
                  <div className="bg-blue-50 border border-blue-200 rounded-lg p-3">
                    <p className="text-sm text-blue-700">
                      <strong>{ticimaxEditModal.product.productionInfo.colors.length}</strong> renk varyantı bulundu.
                      Her varyant için ayrı Ticimax Ürün ID girebilirsiniz.
                    </p>
                  </div>
                  <div className="space-y-3">
                    {ticimaxEditModal.product.productionInfo.colors.map((color) => (
                      <div key={color.code} className="flex items-center gap-3 p-3 bg-gray-50 rounded-lg">
                        <div className="flex-shrink-0 w-32">
                          <span className="text-sm font-medium text-gray-700">{color.name}</span>
                          <span className="text-xs text-gray-500 ml-1">({color.code})</span>
                        </div>
                        <input
                          type="number"
                          value={ticimaxEditData.ticimaxProductIds[color.code] || ''}
                          onChange={(e) => setTicimaxEditData(prev => ({
                            ...prev,
                            ticimaxProductIds: {
                              ...prev.ticimaxProductIds,
                              [color.code]: parseInt(e.target.value) || 0
                            }
                          }))}
                          className="flex-1 px-3 py-2 border border-input rounded-md focus:outline-none focus:ring-2 focus:ring-ring"
                          placeholder="Ticimax ID"
                          min="1"
                        />
                      </div>
                    ))}
                  </div>
                </div>
              ) : (
                <div>
                  <label className="block text-sm font-medium mb-2">
                    Ticimax Ürün ID (UrunKartiID) *
                  </label>
                  <input
                    type="number"
                    value={ticimaxEditData.ticimaxProductId || ''}
                    onChange={(e) => setTicimaxEditData(prev => ({
                      ...prev,
                      ticimaxProductId: parseInt(e.target.value) || 0
                    }))}
                    className="w-full px-3 py-2 border border-input rounded-md focus:outline-none focus:ring-2 focus:ring-ring"
                    placeholder="Örn: 13632"
                    min="1"
                    required
                  />
                </div>
              )}

              <div className="flex gap-3 pt-6 border-t">
                <Button
                  onClick={handleTicimaxSave}
                  disabled={
                    ticimaxSaving || (
                      ticimaxEditModal.product.productionInfo?.colors?.length
                        ? Object.values(ticimaxEditData.ticimaxProductIds).filter(id => id > 0).length === 0
                        : !ticimaxEditData.ticimaxProductId
                    )
                  }
                  className="flex-1"
                >
                  {ticimaxSaving ? (
                    <RefreshCw className="w-4 h-4 mr-2 animate-spin" />
                  ) : (
                    <CheckCircle className="w-4 h-4 mr-2" />
                  )}
                  {ticimaxSaving ? 'Kaydediliyor...' : 'Kaydet'}
                </Button>
                <Button
                  variant="outline"
                  onClick={() => setTicimaxEditModal({ show: false, product: null })}
                  className="px-8"
                >
                  İptal
                </Button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default ProductTable;
